import { create } from 'zustand';
import { api, setGameIdProvider, setGameUnavailableHandler } from '../services/api';

/**
 * Active-game store (#618, epic #600).
 *
 * The admin SPA is ONE deployment serving every registered game; the active
 * game is threaded onto every API call as the `X-Game-Id` header, which the
 * server's resolveGame middleware binds before any admin controller runs.
 * Admin AUTH stays platform-scoped (roles are global — locked decision);
 * only the acted-on data is per-game.
 *
 * The persisted choice is a UX convenience, not a credential — localStorage
 * is fine (same rule as `wc_admin_session_hint`).
 */
export interface AdminGameInfo {
  gameId: string;
  displayName: string;
  enabled: boolean;
  processEnabled: boolean;
  /** Admin surfaces the game's module declares (#619). */
  surfaces?: string[];
  /** The server's default game (`DEFAULT_GAME_ID`) — the snap-back target. */
  isDefault?: boolean;
}

const STORAGE_KEY = 'wc_admin_active_game';
/**
 * "No game chosen": the api client then sends no `X-Game-Id`, and the server
 * resolves the request to ITS default game. The SPA never hardcodes which
 * game that is — `loadGames` learns it from `isDefault`.
 */
const NO_GAME = '';

interface GameState {
  activeGameId: string;
  games: AdminGameInfo[];
  gamesLoaded: boolean;
  setActiveGame: (gameId: string) => void;
  loadGames: () => Promise<void>;
}

function readPersisted(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? NO_GAME;
  } catch {
    return NO_GAME;
  }
}

export const useGameStore = create<GameState>((set, get) => ({
  activeGameId: readPersisted(),
  games: [],
  gamesLoaded: false,

  setActiveGame: (gameId: string) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, gameId);
    } catch {
      // Persistence is best-effort.
    }
    set({ activeGameId: gameId });
  },

  loadGames: async () => {
    const { games } = await api<{ games: AdminGameInfo[] }>('/admin/games');
    const current = get().activeGameId;
    // No choice yet, or a persisted choice for a game this build no longer
    // serves (which must not strand the operator on 404s) — adopt the
    // server's default. Note this only runs when the call SUCCEEDS, which is
    // exactly why `resetToDefaultGame` below exists: the poisoned header 404s
    // this request too.
    if (!games.some((g) => g.gameId === current)) {
      get().setActiveGame(games.find((g) => g.isDefault)?.gameId ?? NO_GAME);
    }
    set({ games, gamesLoaded: true });
  },
}));

/**
 * Recovery from a persisted game the server refuses (#618).
 *
 * `X-Game-Id` rides every call including `/auth/admin-login`, so an
 * inadmissible id in localStorage locks the operator out of the console
 * entirely — reload restores it at store construction and there is no
 * successful response left to snap back from. The api client calls this on
 * any `game_not_found` 404, which is the only signal available once every
 * request is failing.
 */
export function resetToDefaultGame(): void {
  if (useGameStore.getState().activeGameId === NO_GAME) return;
  // Dropping the header lets the server pick its default; the next
  // successful `loadGames` names it.
  useGameStore.getState().setActiveGame(NO_GAME);
}

/** Header provider for the api client (mirrors the tokenProvider pattern). */
export function activeGameIdForApi(): string | null {
  return useGameStore.getState().activeGameId || null;
}

// Wire the api client → active game header (#618). Module-load wiring,
// mirroring authStore's provider registration below its store definition.
setGameIdProvider(activeGameIdForApi);
setGameUnavailableHandler(resetToDefaultGame);
